import { Entity } from '@backstage/catalog-model';
import { isComponentType, isKind } from '@backstage/plugin-catalog';
import { isKubernetesAvailable } from '@backstage/plugin-kubernetes';

import { isAPIDocsDontDisplay, isSnykVisible } from './annotations';

/**
 * Entity-page content-tab filter predicates. Each predicate reproduces the
 * `EntitySwitch`/`if` visibility condition of the corresponding legacy
 * `EntityLayout.Route`, so behaviour is traceable 1:1.
 *
 * They are factored out (rather than inlined in `entityPage.tsx`) so they can
 * be unit tested directly against representative entities.
 *
 * (The Overview tab's own annotation-based card conditionals - SonarQube/
 * Snyk/JFrog/Links/Subcomponents - are explicit JSX in
 * `./content/OverviewContent`, not extension `filter`s, since that content is
 * a single hand-authored tree rather than several independently-discovered
 * cards. They still reuse the same `./annotations` helpers as here.)
 */

export const isComponentEntity = isKind('component');
export const isSystemEntity = isKind('system');

/** CI/CD tab: service / website / iac components (legacy: NOT library). */
export const isCicdComponent = (entity: Entity): boolean =>
  isComponentEntity(entity) &&
  ['service', 'website', 'iac'].some(t => isComponentType(t)(entity));

/** Kubernetes tab: service / website components where Kubernetes is available. */
export const isKubernetesContentVisible = (entity: Entity): boolean =>
  isComponentEntity(entity) &&
  (isComponentType('service')(entity) || isComponentType('website')(entity)) &&
  isKubernetesAvailable(entity);

/** API tab: service components without the apidocs-dont-display suppressor. */
export const isApiContentVisible = (entity: Entity): boolean =>
  isComponentEntity(entity) &&
  isComponentType('service')(entity) &&
  !isAPIDocsDontDisplay(entity);

/** Snyk tab: service / library / website / iac components where Snyk is visible. */
export const isSnykContentVisible = (entity: Entity): boolean =>
  isComponentEntity(entity) &&
  (isComponentType('service')(entity) ||
    isComponentType('library')(entity) ||
    isComponentType('website')(entity) ||
    isComponentType('iac')(entity)) &&
  isSnykVisible(entity);

